import { signal } from '@preact/signals-core';
import {
  getAuthHeaders,
  assetsListSignal,
  customersSignal,
  sitesSignal,
  deviceModelsSignal,
  pageTypesSignal
} from './assets';
import { consumablesSignal, refreshConsumables } from './device_models';

// ─── Signals ───────────────────────────────────────────────────────────────────

export const nonMpsAssetIdSignal = signal<string | null>(null);
export const nonMpsDataSignal = signal<any | null>(null);
export const nonMpsLoadingSignal = signal<boolean>(false);
export const nonMpsErrorSignal = signal<string | null>(null);
export const nonMpsExpandedTicketSignal = signal<string | null>(null);

export function initNonMpsSignals() {
  nonMpsDataSignal.value = null;
  nonMpsErrorSignal.value = null;
  nonMpsExpandedTicketSignal.value = null;
}

export async function refreshNonMpsDetail(assetId: string) {
  if (!assetId) return;
  nonMpsAssetIdSignal.value = assetId;
  nonMpsLoadingSignal.value = true;
  nonMpsErrorSignal.value = null;

  try {
    const res = await fetch(`/api/v1/assets/${assetId}/analytics`, {
      headers: getAuthHeaders(),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.isMps) {
        console.warn(`[Router] Asset ${assetId} is an MPS device. Redirecting to device-analytics...`);
        window.location.hash = `#device-analytics?id=${assetId}`;
        return;
      }
      if (consumablesSignal.value.length === 0) {
        await refreshConsumables();
      }
      nonMpsDataSignal.value = data;
    } else {
      let errMsg = 'Failed to fetch device details.';
      try {
        const text = await res.text();
        if (text) {
          try {
            const errData = JSON.parse(text);
            errMsg = errData.error || errData.message || errMsg;
          } catch {
            errMsg = text;
          }
        } else {
          if (res.status === 404) {
            errMsg = 'Device not found.';
          } else if (res.status === 403) {
            errMsg = 'Access denied. You do not have permission to view this device.';
          } else if (res.status === 401) {
            errMsg = 'Session expired. Please log in again.';
          }
        }
      } catch (readErr) {
        console.error('Failed to read error response:', readErr);
      }
      nonMpsErrorSignal.value = errMsg;
      nonMpsDataSignal.value = null;
    }
  } catch (err) {
    console.error('Failed to fetch non-MPS device details', err);
    nonMpsErrorSignal.value = err instanceof Error ? err.message : 'Error: network failure.';
    nonMpsDataSignal.value = null;
  } finally {
    nonMpsLoadingSignal.value = false;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDateGB(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
}

function formatDateTimeGB(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return 'Active';
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const hours = Math.floor(diffSecs / 3600);
  const minutes = Math.floor((diffSecs % 3600) / 60);
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (parts.length === 0) parts.push('<1m');
  return parts.join(' ');
}

// ─── Page Renderer ─────────────────────────────────────────────────────────────

export function renderNonMpsDetailPage(): string {
  const loading = nonMpsLoadingSignal.value;
  const error = nonMpsErrorSignal.value;
  const data = nonMpsDataSignal.value;

  if (loading) {
    return `
      <s-page heading="Service Only Asset" inlineSize="large">
        <s-stack gap="base" alignment="center" style="padding: 5rem 0;">
          <s-spinner size="large"></s-spinner>
          <s-text tone="subdued">Loading service asset profile…</s-text>
        </s-stack>
      </s-page>
    `;
  }

  if (error) {
    return `
      <s-page heading="Service Only Asset" inlineSize="large">
        <div style="padding: 1rem 0;">
          <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem;">
            <a href="#assets" style="color: var(--p-color-text-link, #008060); text-decoration: none; font-size: 0.875rem;">&larr; Back to Asset Explorer</a>
          </div>
          <s-banner tone="critical" title="Error">
            <p>${error}</p>
          </s-banner>
        </div>
      </s-page>
    `;
  }

  if (!data) {
    return `
      <s-page heading="Service Only Asset" inlineSize="large">
        <div style="padding: 1rem 0;">
          <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem;">
            <a href="#assets" style="color: var(--p-color-text-link, #008060); text-decoration: none; font-size: 0.875rem;">&larr; Back to Asset Explorer</a>
          </div>
          <s-banner tone="attention" title="Asset Unavailable">
            <p>No data loaded. Please select an asset from the Asset Explorer.</p>
          </s-banner>
        </div>
      </s-page>
    `;
  }

  // Resolve asset metadata
  const assetId = nonMpsAssetIdSignal.value;
  const asset = (assetsListSignal.value || []).find((a: any) => a.id === assetId);

  let serialNumber = 'Unknown';
  let modelName = 'Unknown';
  let siteName = 'Unknown';
  let siteAddress = '—';
  let customerName = 'Unknown';
  let status = 'Unknown';
  let locationStr = '';

  if (asset) {
    serialNumber = asset.serialNumber || 'Unknown';
    status = asset.status || 'Unknown';

    const modelObj = (deviceModelsSignal.value || []).find((m: any) => m.id === asset.modelId);
    if (modelObj) {
      modelName = `${modelObj.vendor} ${modelObj.modelName}`;
    }

    const siteObj = (sitesSignal.value || []).find((s: any) => s.id === asset.siteId);
    if (siteObj) {
      siteName = siteObj.name;
      siteAddress = siteObj.address || '—';
    }

    const customerObj = (customersSignal.value || []).find((c: any) => c.id === asset.customerId);
    if (customerObj) {
      customerName = customerObj.name;
    }

    if (asset.latitude != null && asset.longitude != null) {
      locationStr = `${Number(asset.latitude).toFixed(5)}, ${Number(asset.longitude).toFixed(5)}`;
    }
  }

  let statusTone = 'info';
  if (status === 'active') statusTone = 'success';
  if (status === 'error') statusTone = 'critical';

  // Ticket & session history section
  const ticketsData = data.tickets || [];
  const expandedTicketId = nonMpsExpandedTicketSignal.value;

  const ticketListHtml = ticketsData.length === 0
    ? `<s-text tone="subdued" style="font-style:italic;">No tickets recorded for this device.</s-text>`
    : ticketsData.map((t: any) => {
      const isExpanded = expandedTicketId === t.id;
      const sessionsHtml = (t.sessions || []).map((s: any) => `
        <div style="padding:0.5rem 0.75rem;background:var(--p-color-bg-surface,#fff);border-radius:4px;border:1px solid var(--p-color-border-subdued,#e1e3e5);margin-bottom:0.25rem;">
          <s-stack gap="extraSmall">
            <div style="display:flex;justify-content:space-between;align-items:center;">
              <s-text style="font-size:0.85rem;font-weight:600;">${s.technicianName || 'Technician'}</s-text>
              <s-text tone="subdued" style="font-size:0.8rem;">${formatDuration(s.startedAt, s.endedAt)}</s-text>
            </div>
            <s-text tone="subdued" style="font-size:0.8rem;">${formatDateTimeGB(s.startedAt)}${s.endedAt ? ' — ' + formatDateTimeGB(s.endedAt) : ''}</s-text>
            ${s.summary ? `<s-text style="font-size:0.85rem;">${s.summary}</s-text>` : ''}
            ${s.counters && Object.keys(s.counters).length > 0 ? `
              <div style="font-size:0.8rem;color:var(--p-color-text-subdued);">
                Counters: ${Object.entries(s.counters).map(([k, v]) => `${k}: <strong>${(v as number).toLocaleString()}</strong>`).join(' · ')}
              </div>
            ` : ''}
          </s-stack>
        </div>
      `).join('');

      return `
        <s-box padding="base" background="surface-secondary" borderRadius="base" style="margin-bottom:0.5rem;cursor:pointer;">
          <div id="nonmps-ticket-${t.id}">
            <div style="display:flex;justify-content:space-between;align-items:center;">
              <s-stack gap="extraSmall">
                <s-stack direction="inline" gap="small" align="center">
                  <s-text style="font-weight:600;">${t.ticketNumber}</s-text>
                  <s-badge tone="${t.status === 'resolved' || t.status === 'closed' ? 'success' : t.status === 'open' ? 'info' : 'attention'}">${t.status}</s-badge>
                  <s-badge tone="${t.priority === 'critical' ? 'critical' : t.priority === 'high' ? 'warning' : 'info'}">${t.priority}</s-badge>
                </s-stack>
                <s-text tone="subdued" style="font-size:0.8rem;">${t.description || 'No description'}</s-text>
              </s-stack>
              <s-stack gap="extraSmall" alignment="trailing">
                <s-text tone="subdued" style="font-size:0.8rem;">Opened: ${formatDateGB(t.createdAt)}</s-text>
                ${t.closedAt ? `<s-text tone="subdued" style="font-size:0.8rem;">Closed: ${formatDateGB(t.closedAt)}</s-text>` : ''}
                ${t.technicians && t.technicians.length > 0 ? `<s-text tone="subdued" style="font-size:0.75rem;">${t.technicians.join(', ')}</s-text>` : ''}
              </s-stack>
            </div>
            ${isExpanded && (t.sessions || []).length > 0 ? `
              <div style="margin-top:0.75rem;padding-top:0.75rem;border-top:1px solid var(--p-color-border-subdued,#e1e3e5);">
                <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);margin-bottom:0.5rem;">Visit Sessions</s-text>
                ${sessionsHtml}
              </div>
            ` : ''}
          </div>
        </s-box>
      `;
    }).join('');

  return `
    <s-page heading="${serialNumber} — Service Only Asset" inlineSize="large">
      <div style="padding: 0.25rem 0 5rem 0;">
        <!-- Breadcrumbs -->
        <div style="display:flex; align-items:center; gap:0.5rem; margin-bottom:1rem; flex-wrap:wrap;">
          <a href="#assets" style="color:var(--p-color-text-link, #008060); text-decoration:none; font-size:0.875rem; font-weight:500;">Asset Explorer</a>
          <span style="color:var(--p-color-text-subdued); font-size:0.875rem;">&gt;</span>
          <s-text style="font-weight:600; font-size:0.875rem;">${serialNumber}</s-text>
          <s-badge tone="${statusTone}">${status.toUpperCase()}</s-badge>
          <s-badge tone="attention">SERVICE ONLY</s-badge>
        </div>

        <!-- Asset Identity Header -->
        <s-section>
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-grid gridTemplateColumns="1fr 1fr 1fr" gap="base">
              <s-stack gap="none">
                <s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Customer</s-text>
                <s-text style="font-weight:600; font-size:0.95rem;">${customerName}</s-text>
              </s-stack>
              <s-stack gap="none">
                <s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Model</s-text>
                <s-text style="font-weight:600; font-size:0.95rem;">${modelName}</s-text>
              </s-stack>
              <s-stack gap="none">
                <s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Site</s-text>
                <s-text style="font-weight:600; font-size:0.95rem;">${siteName}</s-text>
              </s-stack>
            </s-grid>
          </s-box>
        </s-section>

        <!-- Card 1: Customer & Site Details -->
        <s-section style="margin-top: 1.5rem;">
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-stack gap="base">
              <s-heading element="h3">Customer & Site Details</s-heading>
              <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                <s-stack gap="small">
                  <s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Customer Name</s-text>
                  <s-text style="font-weight:600;">${customerName}</s-text>
                </s-stack>
                <s-stack gap="small">
                  <s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Site Name</s-text>
                  <s-text style="font-weight:600;">${siteName}</s-text>
                </s-stack>
                <s-stack gap="small">
                  <s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Address</s-text>
                  <s-text>${siteAddress}</s-text>
                </s-stack>
                <s-stack gap="small">
                  <s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">GPS Coordinates</s-text>
                  <s-text>${locationStr || 'Not recorded'}</s-text>
                </s-stack>
              </s-grid>
            </s-stack>
          </s-box>
        </s-section>

        <!-- Card 2: Replaced Parts History -->
        <s-section style="margin-top: 1.5rem;">
          <s-box padding="none" background="surface" border="base" borderRadius="base">
            <div style="padding: 1rem 1rem 0.5rem 1rem;">
              <s-heading element="h3">Replaced Parts History</s-heading>
              <s-text tone="subdued">Parts and consumables replaced via ticket service sessions. Yields calculated from counter readings.</s-text>
            </div>
            <opm-data-table
              id="nonmps-parts-table"
              searchable="false"
              paginated="true"
              page-size="10"
              empty-message="No parts replacements recorded for this device."
            ></opm-data-table>
          </s-box>
        </s-section>

        <!-- Card 3: Ticket & Service Session History -->
        <s-section style="margin-top: 1.5rem;">
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-stack gap="base">
              <s-stack gap="none">
                <s-heading element="h3">Ticket & Service History</s-heading>
                <s-text tone="subdued">All tickets raised for this asset. Click a ticket to expand visit session details.</s-text>
              </s-stack>
              <div id="nonmps-tickets-list" style="max-height:500px;overflow-y:auto;">
                ${ticketListHtml}
              </div>
            </s-stack>
          </s-box>
        </s-section>

        <!-- Card 4: Meter Readings -->
        <s-section style="margin-top: 1.5rem;">
          <s-box padding="none" background="surface" border="base" borderRadius="base">
            <div style="padding: 1rem 1rem 0.5rem 1rem;">
              <s-heading element="h3">Meter Readings</s-heading>
              <s-text tone="subdued">Manual counter readings captured by technicians during service visits.</s-text>
            </div>
            <opm-data-table
              id="nonmps-readings-table"
              searchable="false"
              paginated="true"
              page-size="10"
              empty-message="No meter readings recorded for this device."
            ></opm-data-table>
          </s-box>
        </s-section>
      </div>
    </s-page>
  `;
}

// ─── Page Listeners ────────────────────────────────────────────────────────────

export function setupNonMpsDetailListeners() {
  const data = nonMpsDataSignal.value;
  const assetId = nonMpsAssetIdSignal.value;
  if (!data || !assetId) return;

  // 1. Populate parts table
  const partsTable = document.getElementById('nonmps-parts-table') as any;
  if (partsTable) {
    partsTable.columns = [
      {
        key: 'installDate',
        header: 'Install Date',
        sortable: true,
        sortType: 'string',
        render: (row: any) => formatDateGB(row.installDate),
      },
      {
        key: 'endDate',
        header: 'Replaced Date',
        render: (row: any) => row.endDate
          ? formatDateGB(row.endDate)
          : '<s-badge tone="success">Active</s-badge>',
      },
      { key: 'consumableCode', header: 'Part Code', sortable: true, sortType: 'string' },
      { key: 'consumableName', header: 'Description', sortable: true, sortType: 'string' },
      {
        key: 'startPageCount',
        header: 'Start Counter',
        alignment: 'right',
        render: (row: any) => row.startPageCount != null ? row.startPageCount.toLocaleString() : '—',
      },
      {
        key: 'endPageCount',
        header: 'End Counter',
        alignment: 'right',
        render: (row: any) => row.endPageCount != null ? row.endPageCount.toLocaleString() : '—',
      },
      {
        key: 'yieldPages',
        header: 'Yield',
        alignment: 'right',
        render: (row: any) => row.yieldPages
          ? `<strong>${row.yieldPages.toLocaleString()} pgs</strong>`
          : '<span style="color:var(--p-color-text-subdued);">—</span>',
      },
    ];
    partsTable.items = data.consumableHistory || [];
  }

  // 2. Populate readings table
  const readingsTable = document.getElementById('nonmps-readings-table') as any;
  if (readingsTable) {
    const pageTypes = pageTypesSignal.value || [];
    const ptMap = new Map(pageTypes.map(pt => [pt.id, pt.displayName]));

    readingsTable.columns = [
      {
        key: 'createdAt',
        header: 'Date & Time',
        sortable: true,
        sortType: 'string',
        render: (row: any) => formatDateTimeGB(row.createdAt),
      },
      { key: 'reporterId', header: 'Reporter', sortable: true, sortType: 'string' },
      {
        key: 'counters',
        header: 'Counter Volumes',
        render: (row: any) => {
          const cnts = row.counters || {};
          return Object.entries(cnts).map(([ptId, val]) => {
            const name = ptMap.get(ptId) || ptId;
            return `<div style="font-size:0.875rem;">${name}: <strong>${(val as number).toLocaleString()}</strong></div>`;
          }).join('');
        }
      },
      {
        key: 'photoUrl',
        header: 'Evidence',
        render: (row: any) => {
          if (row.photoUrl) {
            return `<a href="${row.photoUrl}" target="_blank" style="color:var(--p-color-text-link);text-decoration:underline;font-size:0.875rem;">View Photo</a>`;
          }
          return '<span style="font-size:0.875rem;color:var(--p-color-text-subdued);">None</span>';
        }
      }
    ];

    // Fetch readings for this asset
    (async () => {
      try {
        const res = await fetch(`/api/v1/assets/${assetId}/readings`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          readingsTable.items = await res.json();
        } else {
          readingsTable.items = [];
        }
      } catch (err) {
        console.error('Failed to fetch readings for non-MPS detail', err);
        readingsTable.items = [];
      }
    })();
  }

  // 3. Ticket expand/collapse click handlers
  const ticketsData = data.tickets || [];
  ticketsData.forEach((t: any) => {
    const ticketEl = document.getElementById(`nonmps-ticket-${t.id}`);
    if (ticketEl) {
      ticketEl.addEventListener('click', () => {
        if (nonMpsExpandedTicketSignal.value === t.id) {
          nonMpsExpandedTicketSignal.value = null;
        } else {
          nonMpsExpandedTicketSignal.value = t.id;
        }
      });
    }
  });
}
